import { cropUrl } from './images.js'

const PLACEHOLDER_IMAGE = '/images/placeholder.jpg'

export const H7_POWERFUL_LED_PRODUCTS = [
  { productId: 2538, errorLevel: 3 },
  { productId: 3612, errorLevel: 4 },
  { productId: 3454, errorLevel: 3 },
  { productId: 1185, errorLevel: 2 },
  { productId: 3287, errorLevel: 1 },
  { productId: 3105, errorLevel: 1 },
]

export const H7_COMPACT_LED_PRODUCTS = [
  { productId: 3569, errorLevel: 2 },
  { productId: 3497, errorLevel: null },
]

export const H7_ALL_PRODUCT_IDS = [
  ...H7_POWERFUL_LED_PRODUCTS,
  ...H7_COMPACT_LED_PRODUCTS,
].map(config => config.productId)

const CACHE_TTL = 10 * 60 * 1000
const cache = new Map()

const slimImage = ({ url, url_template, extension, is_pack_image }) => ({
  url,
  url_template,
  extension,
  is_pack_image,
})

export const h7ProductImage = (variant, size) => {
  const image = variant.images?.[0]
  if (!image) {
    return PLACEHOLDER_IMAGE
  }
  return cropUrl(image, size, size, image.extension, 80) || image.url
}

const slimProduct = product => {
  if (!product || !product.variant) {
    return null
  }
  const variant = product.variant
  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    brand: product.brand ? { name: product.brand.name } : null,
    variant: {
      gtin: variant.gtin,
      reference: variant.reference,
      price_ttc_base: variant.price_ttc_base,
      watts: variant.watts,
      lumens: variant.lumens,
      luminosity_gain: variant.luminosity_gain,
      generic_type: variant.generic_type
        ? { type: variant.generic_type.type }
        : null,
      images: variant.images?.length ? [slimImage(variant.images[0])] : [],
    },
  }
}

const fetchProducts = async productService => {
  const response = await productService.fetch({
    'filter[operation]': `where:id;in;${H7_ALL_PRODUCT_IDS.join(';')}`,
    include: 'images,brand,variant.images',
    limit: H7_ALL_PRODUCT_IDS.length,
    total: 0,
  })
  const products = {}
  ;(response.data || []).forEach(product => {
    const slim = slimProduct(product)
    if (slim) {
      products[product.id] = slim
    }
  })
  return products
}

export const loadH7ComparisonProducts = async (productService, cacheKey) => {
  const cached = cache.get(cacheKey)
  if (cached && cached.expiresAt > Date.now()) {
    return cached.promise
  }
  const promise = fetchProducts(productService)
  cache.set(cacheKey, { promise, expiresAt: Date.now() + CACHE_TTL })
  try {
    const products = await promise
    if (!Object.keys(products).length) {
      cache.delete(cacheKey)
    }
    return products
  } catch (error) {
    cache.delete(cacheKey)
    throw error
  }
}

const extractColorTemperature = product => {
  const match = product.name?.match(/(\d{4,5})K/)
  return match ? match[1] + 'K' : null
}

export const buildH7ProductRow = (product, config, localePath) => {
  if (!product || !product.variant) {
    return null
  }

  const variant = product.variant

  const imageUrl = h7ProductImage(variant, 160)

  const price = variant.price_ttc_base || 0
  const formattedPrice = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(price)

  const watts = variant.watts || 0
  const lumens = variant.lumens || 0
  const luminosityGain = variant.luminosity_gain || 0

  const lumensDisplay = `${lumens.toLocaleString('fr-FR')} lm / ${watts} W`

  const gainNumeric = Math.round(luminosityGain)

  return {
    productId: config.productId,
    img: imageUrl,
    model: product.name,
    type: variant.generic_type?.type || 'H7',
    temp: extractColorTemperature(product) || '6500 K',
    lumens: lumensDisplay,
    price: formattedPrice,
    gain: `+${gainNumeric} %`,
    gainNumeric,
    errorLevel: config.errorLevel,
    slug: product.slug,
    link: localePath({
      name: 'produit-slug',
      params: { slug: product.slug },
    }),
  }
}
